import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import useLenis from "@/hooks/useLenis";
import SplashLoader from "@/components/SplashLoader";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Pillars from "@/components/Pillars";
import EditorialImage from "@/components/EditorialImage";
import Collection from "@/components/Collection";
import Journal from "@/components/Journal";
import Playlist from "@/components/Playlist";
import Community from "@/components/Community";
import Newsletter from "@/components/Newsletter";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";

import { ASSETS } from "@/lib/config";

import Admin from "@/pages/Admin";

/*
  Color flow (top → bottom) — black → grey → blue → green → back
  hero            #0a0a0a
  about           #0f1216
  edt I (blueH)   #14242e (deep slate-blue)
  pillars         #101318
  edt II (bball)  #0f2231 (deep blue)
  collection      #101820 (slate)
  edt III (foot)  #142e25 (forest green)
  journal         #0f1216
  playlist        #0a0e14
  edt IV (col)    #1a3a44 (teal)
  community       #c9d3d8 (fog light — cool)
  edt V (sunset)  #14304a (navy)
  newsletter      #0a0a0a
  contact         #0f1216
  footer          #050505
*/

const Home = () => {
  useLenis();

  return (
    <div className="bg-[#0a0a0a] text-[#e8ecef] selection:bg-[#e8ecef] selection:text-[#0a0a0a]">
      <SplashLoader />
      <Navigation />

      <main>
        <Hero />
        <About />

        {/* Editorial marquee — signature slow ticker */}
        <div
          className="relative border-y border-[#c9d3d8]/10 py-8 md:py-10 overflow-hidden"
          style={{
            backgroundImage:
              "linear-gradient(to bottom, #14242e 0%, #101318 100%)",
          }}
        >
          <Marquee
            data-testid="editorial-marquee"
            duration={52}
            items={[
              "LIVE WITH VISION",
              "SLOW DOWN",
              "STRENGTHEN THE BODY",
              "EXPAND THE MIND",
              "BUILD MEANINGFUL BONDS",
              "PURSUE PURPOSE",
              "PROTECT YOUR PEACE",
              "STAY PRESENT",
            ]}
            textClassName="font-serif text-[#e8ecef] text-5xl md:text-7xl lg:text-8xl leading-none tracking-tight font-light"
            separator="✦"
          />
        </div>

        <EditorialImage
          src={ASSETS.blueHallway}
          alt="A quiet corridor — the vision within"
          chapter="I."
          eyebrow="Vision · Interior"
          quote="Every discipline begins where the noise ends."
          caption="A pause between reps. Between thoughts. The room quiets, and the work begins in earnest."
          panel="#14242e"
          fromColor="#0f1216"
          toColor="#101318"
          testId="editorial-image-vision"
        />

        <Pillars />

        <EditorialImage
          src={ASSETS.basketball}
          alt="Deliberate practice"
          chapter="II."
          eyebrow="Discipline · Repetition"
          quote="The way you do one thing is the way you do everything."
          caption="Mastery is not a moment. It is ten thousand quiet ones, strung together with attention."
          panel="#0f2231"
          fromColor="#101318"
          toColor="#101820"
          reverse
          testId="editorial-image-discipline"
        />

        <Collection />

        <EditorialImage
          src={ASSETS.football}
          alt="Warm-up at first light"
          chapter="III."
          eyebrow="Training · Ritual"
          quote="Show up. Especially on the days you don't feel like it."
          caption="Ritual is the shape of intention. What you do at dawn becomes who you are at dusk."
          panel="#142e25"
          fromColor="#101820"
          toColor="#0f1216"
          testId="editorial-image-training"
        />

        <Journal />
        <Playlist />

        <EditorialImage
          src={ASSETS.columnsPortrait}
          alt="Considered stillness in architecture"
          chapter="IV."
          eyebrow="Simplicity · Clarity"
          quote="Less, but better. Slower, but truer."
          caption="Between the columns — the truest architecture is what you choose to leave out."
          panel="#1a3a44"
          fromColor="#0a0e14"
          toColor="#c9d3d8"
          reverse
          testId="editorial-image-simplicity"
        />

        <Community />

        <EditorialImage
          src={ASSETS.sunsetOcean}
          alt="Golden hour over open water"
          chapter="V."
          eyebrow="Presence · Peace"
          quote="The most radical act is to be still, and listen."
          caption="A closing note. The horizon does not rush. Neither should you."
          panel="#14304a"
          fromColor="#c9d3d8"
          toColor="#0a0a0a"
          testId="editorial-image-peace"
        />

        <Newsletter />
        <Contact />
      </main>

      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
